import React, {ReactNode, forwardRef, useEffect, useState, useMemo, useId, useRef} from 'react'
import {FormControl, Text, FormErrorMessage, useMultiStyleConfig, FormControlProps, TextProps, Box} from '@chakra-ui/react'
import {Select as ChakraSelect} from 'chakra-react-select'

interface SelectProps {
    name: string
    label: string | ReactNode
    placeholder?: string | ReactNode
    options: {label: string; value: string}[]
    isRequired?: boolean
    value?: string
    variant?: 'sortBy'
    defaultValue?: string
    errorMessage?: string
    dataTestId?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    isSearchable?: boolean
    menuPortalTarget?: HTMLElement | null
    menuPlacement?: 'auto' | 'top' | 'bottom'
    onChange?: (value: string | null) => void
    onOptionSelected?: (value: string | null) => void
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void
}

/**
 * @name Select
 *
 * @component
 *
 * @description
 * A custom `Select` component built using Chakra UI's `FormControl` and `ChakraSelect`.
 * This component supports various styles and configurations based on the provided props.
 *
 * @param {SelectProps} props - Properties object.
 * @param {string} props.name - The name of the select input.
 * @param {string | ReactNode} [props.label] - The label for the select input.
 * @param {string | ReactNode} [props.placeholder='Select...'] - The placeholder text for the select input.
 * @param {Array} props.options - The options to be displayed in the select dropdown.
 * @param {boolean} [props.isRequired] - Whether the select input is required.
 * @param {string} [props.value] - The current value of the select input.
 * @param {string} [props.variant] - The variant of the select input, used to determine styles.
 * @param {string} [props.defaultValue] - The default value of the select input.
 * @param {string} [props.errorMessage] - The error message to be displayed if the input is invalid.
 * @param {boolean} [props.isDisabled] - Whether the select input is disabled.
 * @param {boolean} [props.isReadOnly] - Whether the select input is read-only.
 * @param {boolean} [props.isSearchable] - Whether the select input is searchable.
 * @param {HTMLElement} [props.menuPortalTarget] - The element to append the menu to (for use inside modals).
 * @param {function} [props.onChange] - Callback function to handle change events.
 * @param {function} [props.onOptionSelected] - Callback function to handle change events.
 * @param {function} [props.onBlur] - Callback function to handle blur events.
 * @param {React.Ref<HTMLDivElement>} props.ref - The ref to be forwarded to the `FormControl` component.
 *
 * @returns {JSX.Element} The rendered `Select` component.
 */
const Select = forwardRef<HTMLDivElement, SelectProps>(
    (
        {
            name,
            label,
            placeholder = 'Select...',
            options,
            isRequired,
            value,
            variant,
            defaultValue,
            dataTestId = 'select',
            errorMessage,
            isDisabled,
            isReadOnly,
            isSearchable = false,
            menuPortalTarget,
            menuPlacement = 'auto',
            onChange,
            onOptionSelected,
            onBlur,
        },
        ref,
    ) => {
        const selectRef = useRef<HTMLDivElement | null>(null)
        const [inputSequence, setInputSequence] = useState<string>('')
        const [highlightIndex, setHighlightIndex] = useState<number>(-1)
        const [keyTimeout, setKeyTimeout] = useState<NodeJS.Timeout | null>(null)
        const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)
        const styles = useMultiStyleConfig(variant === 'sortBy' ? 'SortBySelect' : 'Select', {menuPlacement}) as Record<string, FormControlProps & TextProps>

        const chakraStyles = {
            container: (provided) => ({
                ...provided,
                ...styles.container,
            }),
            control: () => ({
                ...styles.control,
            }),
            menu: (provided) => ({
                ...provided,
                ...styles.menu,
            }),
            menuList: (provided) => ({
                ...provided,
                ...styles.menuList,
            }),
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999,
            }),
            indicatorSeparator: (provided) => ({
                ...provided,
                ...styles.indicatorSeparator,
            }),
            dropdownIndicator: (provided, state) => ({
                ...provided,
                ...styles.dropdownIndicator,
                transition: 'all .4s ease-in-out',
                transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : '',
            }),
            downChevron: () => ({
                ...styles.downChevron,
            }),
            option: (provided) => ({
                ...provided,
                ...styles.option,
            }),
            input: (provided) => ({
                ...provided,
                ...styles.input,
            }),
            singleValue: (provided) => ({
                ...provided,
                ...styles.singleValue,
            }),
            placeholder: (provided) => ({
                ...provided,
                ...styles.placeholder,
            }),
        }

        const handleKeyDown = (event) => {
            const key = event.key.toLowerCase()
            if ((event.key === 'ArrowDown' || event.key === 'Enter') && !menuIsOpen) {
                setMenuIsOpen(true)
            }
            if (key.length === 1 && key.match(/[a-z]/i)) {
                const newInputSequence = inputSequence + key
                setInputSequence(newInputSequence)

                if (keyTimeout) {
                    clearTimeout(keyTimeout)
                }
                setKeyTimeout(
                    setTimeout(() => {
                        const matchingOptions = indexedOptions.filter((option) => option.label.toLowerCase().startsWith(newInputSequence))
                        if (matchingOptions.length > 0) {
                            setHighlightIndex(matchingOptions[0].index)
                            if (matchingOptions[0] && onChange) {
                                onChange(matchingOptions[0].value)
                            }
                        } else {
                            setHighlightIndex(-1)
                        }
                        setInputSequence('')
                    }, 300),
                )
            } else if (event.key === 'Enter' && highlightIndex >= 0) {
                event.preventDefault()
                const selectedOption = indexedOptions.find((option) => option.index === highlightIndex)
                if (selectedOption && onChange) {
                    onChange(selectedOption.value)
                }
                ;(document.activeElement as HTMLElement)?.blur()
            } else {
                setHighlightIndex(-1)
            }
        }

        const indexedOptions = options.map((option, index) => ({...option, index}))

        const ariaLabel = useMemo(() => {
            return (
                indexedOptions?.find((option) => option.value === value)?.label ?? // Selected option
                indexedOptions?.find((option) => option.value === defaultValue)?.label ?? // Default option
                (typeof label === 'string' // Label in case it is a string
                    ? label
                    : typeof placeholder === 'string' // Placeholder in case it is a string
                      ? placeholder
                      : undefined) // Undefined otherwise
            )
        }, [indexedOptions, value])

        useEffect(() => {
            if (highlightIndex >= 0) {
                const selectCountryElement = document.querySelector(`[data-testid="${dataTestId}"] input`)

                // Get the value of the aria-controls attribute
                const listboxId = selectCountryElement?.getAttribute('aria-controls')

                // Use the aria-controls value to find the listbox element
                if (listboxId) {
                    const listboxElement = document.getElementById(listboxId)

                    const highlightedOption = listboxElement?.querySelector(`[id^="react-select-"][id$="-option-${highlightIndex}"]`)
                    if (highlightedOption) {
                        highlightedOption.scrollIntoView({behavior: 'smooth', block: 'nearest'})
                    }
                }
            }
        }, [highlightIndex])

        /**
         * Removes aria-orientation attribute from rendered `hr` inside the select.
         * An external box is applied to retrieve the reference and remove the specific attribute.
         */
        useEffect(() => {
            if (selectRef.current) {
                const divider = selectRef.current.querySelector('.chakra-divider')

                if (divider) {
                    divider.removeAttribute('aria-orientation')
                }
            }
        }, [])

        return (
            <FormControl
                ref={ref}
                id={name}
                isRequired={isRequired}
                isInvalid={!!errorMessage}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                data-testid={dataTestId}
                {...styles.formControl}
            >
                {label && variant !== 'sortBy' && (
                    <Text {...styles.label} size="sm" className="select__label">
                        {label}
                    </Text>
                )}
                <Box ref={selectRef}>
                    <ChakraSelect
                        instanceId={useId()}
                        chakraStyles={chakraStyles}
                        name={name}
                        options={indexedOptions}
                        placeholder={placeholder}
                        value={indexedOptions?.find((option) => option.value === value)}
                        defaultValue={indexedOptions?.find((option) => option.value === defaultValue)}
                        onChange={(selected) => {
                            const value = selected?.value || null
                            if (onChange) onChange(value)
                            if (onOptionSelected) onOptionSelected(value)
                            setMenuIsOpen(false)
                        }}
                        onBlur={(e) => {
                            if (onBlur) onBlur(e)
                            setMenuIsOpen(false)
                        }}
                        isSearchable={isSearchable}
                        menuPlacement={menuPlacement}
                        menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
                        closeMenuOnSelect={true}
                        onKeyDown={handleKeyDown}
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={() => setMenuIsOpen(true)}
                        onMenuClose={() => setMenuIsOpen(false)}
                        menuPortalTarget={menuPortalTarget}
                        aria-label={ariaLabel}
                    />
                </Box>
                {errorMessage && variant !== 'sortBy' && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
            </FormControl>
        )
    },
)

Select.displayName = 'Select'

export {Select}
